import { Ability, AbilityBuilder, AbilityClass } from '@casl/ability'
import SvgIcon from '@mui/material/SvgIcon'
import _ from 'lodash'
import React, { useEffect, PropsWithChildren, createContext, useContext, useState } from 'react'
import { IconType } from 'react-icons'

import {
  AgencySettings_EnabledFeaturesEnum,
  DomainPermission_DomainEnum,
  DomainPermission_VerbEnum,
  Favorite_PageIdEnum,
} from 'src/api/gen'
import { useCheckEnabledFeature } from 'src/layout/AgencySettings'
import { useUserProfile } from 'src/layout/hooks'

import { userHasPermissions, userHasAtLeastOnePermission, getObject, pagesFilter } from './utils'

type AbilityActions = keyof typeof DomainPermission_VerbEnum
type AbilitySubjects = keyof typeof DomainPermission_DomainEnum

type AbilityType = Ability<[AbilityActions, AbilitySubjects]>
const AbilityType = Ability as AbilityClass<AbilityType>

export type Permission = [AbilityActions, AbilitySubjects]

export interface IPage {
  Icon?: IconType | typeof SvgIcon
  ParentIcon?: IconType | typeof SvgIcon
  component: JSX.Element | React.ReactNode
  enabled?: boolean
  featureFlags?: AgencySettings_EnabledFeaturesEnum[]
  label: string
  pageID: Favorite_PageIdEnum
  pages?: IPage[]
  permissions?: Permission[]
  permissionsUnion?: Permission[]
  to: string
}

interface IRouteAuthContext {
  allowedRoutes: Partial<IPage>[]
  getPageByID: (pageID: Favorite_PageIdEnum) => Partial<IPage> | undefined
  routes: Partial<IPage>[]
}

interface IRouteAuthWrapperProps {
  routes?: Partial<IPage>[]
}

const RouteAuthContext = createContext<IRouteAuthContext | undefined>(undefined)

export function useRouteAuth() {
  const ctx = useContext(RouteAuthContext)

  if (!ctx) {
    throw new Error('useRouteAuth have to be inside RouteAuthWrapper')
  }

  return ctx
}
export function RouteAuthWrapper({ routes: initialRoutes = [], children }: PropsWithChildren<IRouteAuthWrapperProps>) {
  const user = useUserProfile()
  const isFeatureEnabled = useCheckEnabledFeature()

  const [ability] = useState<Ability>(new Ability<[AbilityActions, AbilitySubjects]>())
  const { can: allow, rules } = new AbilityBuilder(AbilityType)

  const [routes] = useState(initialRoutes)
  const [allowedRoutes, setAllowedRoutes] = useState<Partial<IPage>[]>([])

  useEffect(() => {
    // Update user rights
    for (const permission of user?.domain_permissions ?? []) {
      if (!permission.verb || !permission.domain) {
        throw new Error(
          `trying to update permissions with invalid action=${permission.verb} topic=${permission.domain}`
        )
      }
      allow(permission.verb, permission.domain)
    }

    ability.update(rules)
  }, [user?.domain_permissions])

  useEffect(() => {
    const filteredRoutes = pagesFilter(routes, (page) => {
      const isEnabled = 'enabled' in page ? page.enabled === true : true
      const areFeaturesEnabled = page?.featureFlags ? page.featureFlags.some(isFeatureEnabled) : true

      return (
        isEnabled &&
        areFeaturesEnabled &&
        userHasPermissions(ability, page?.permissions ?? []) &&
        userHasAtLeastOnePermission(ability, page?.permissionsUnion ?? [])
      )
    })
    setAllowedRoutes(filteredRoutes)
  }, [ability.rules])

  /**
   *
   * @param pageID
   * @returns
   */
  const getPageByID = (pageID: Favorite_PageIdEnum) => {
    return getObject(allowedRoutes, 'pageID', pageID)
  }

  const getValue = () => {
    return {
      routes,
      allowedRoutes,
      getPageByID,
    }
  }

  return <RouteAuthContext.Provider value={getValue()}>{children}</RouteAuthContext.Provider>
}
